import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';

/**
 * Identifies this browser to the server's new-device check (sent as
 * `x-device-fingerprint`). It is a random id kept in localStorage, so it stays
 * the same across browser updates, monitor changes and restarts; a computed
 * browser fingerprint drifted and made trusted browsers ask for the email code
 * again. Clearing site data or a private window counts as a new device.
 */
@Injectable({
    providedIn: 'root',
})
export class FingerprintService {
    private readonly document = inject(DOCUMENT);
    private readonly storageKey = 'qc:device-id';
    // Used only when storage is unavailable.
    private fallbackId: string | null = null;

    getFingerprint(): Observable<string | null> {
        return of(this.deviceId());
    }

    private deviceId(): string {
        try {
            const storage = this.document.defaultView?.localStorage;
            if (storage) {
                // Read on every request so all tabs settle on the same id.
                const stored = storage.getItem(this.storageKey);
                if (stored) return stored;
                const created = this.createId();
                storage.setItem(this.storageKey, created);
                return created;
            }
        } catch {
            /* Storage blocked: keep an id for this page only. */
        }
        return (this.fallbackId ??= this.createId());
    }

    private createId(): string {
        const bytes = new Uint8Array(16);
        crypto.getRandomValues(bytes);
        return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    }
}
